import React, { ReactNode, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty, values } from 'lodash';
import { selectCard } from 'js/actions';
import { AvailableFilters } from 'js/components/MacroInput/types/AvailableFilters';
import DataMapDetailActionsContainer from 'js/components/data_maps/cards/DataMapDetailActionsContainer';
import { DataMapDetailHeader } from 'js/components/data_maps/cards/DataMapDetailHeader';
import TagsView from 'js/components/elements/tags/TagsView';
import { RESOURCE_TYPE_DATA_MAP } from 'js/constants/resourceTypes';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { useListOldReduxContainer } from 'new/elements/DataCard/useListOldReduxContainer';
import { mapStateToProps } from 'js/components/data_maps/cards/stateMapper';
import DateUtils from 'js/lib/DateUtils';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { DetailsIconButton } from 'new/elements/Button';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  view: SearchFilterViewsType;
  detailViewTabs: any[];
  getSourceMeta: (id: number) => any;
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  noContentView: ReactNode;
};

const makeRowRenderer =
  ({ handleCardSelected, getData }) =>
  (index, item) => {
    const { name, description, createdAt, readOnly, aclQueryMode, owner, cardData } =
      getData(index);
    const readOnlyLabel = shouldDisplayOwnershipLabel(aclQueryMode)
      ? ownershipLabel(cardData)
      : null;

    return (
      <DataCard
        key={item.id || index}
        onClick={() => handleCardSelected(cardData)}
        rowsSlots={[
          <>
            <NameDescCell name={name} description={description} />
            {readOnlyLabel && (
              <DataCardCell contextualInfoCell>
                <Typography variant="caption">{readOnlyLabel}</Typography>
                {!isOwner(cardData) && (
                  <Typography variant="body3">
                    <b>OWNED BY: </b> {owner.email}
                  </Typography>
                )}
              </DataCardCell>
            )}
            <DataCardCell rightCTAsCell>
              <DetailsIconButton onClick={() => handleCardSelected(cardData)} />
              <DataMapDetailActionsContainer
                data={cardData}
                openDetailsModal={() => handleCardSelected(cardData)}
                readOnly={readOnly}
              />
            </DataCardCell>
          </>,
          !isEmpty(cardData.tags) && (
            <DataCardCell>
              <TagsView data={cardData} resourceType={RESOURCE_TYPE_DATA_MAP} />
            </DataCardCell>
          ),
          <DataCardCell multiCol>
            <Stack spacing="8px" direction="row" alignItems="flex-end">
              <ChipWithCopy tooltipTitle="Copy Lookup ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <Typography variant="body3">
                <b>CREATED ON:</b> {createdAt}
              </Typography>
            </Stack>
            <Typography
              variant="overline"
              color={cardData.data_set_id ? 'primary.main' : 'primary.dark'}
            >
              {cardData.data_set_id ? 'Dynamic Lookup' : 'Static Lookup'}
            </Typography>
          </DataCardCell>,
        ].filter(Boolean)}
      />
    );
  };

const DataMapTable = ({
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  detailViewTabs,
  getSourceMeta,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useAppDispatch();
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId, aclQueryMode } = listing;
  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);
  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useListOldReduxContainer(
    data,
    mapStateToProps,
    (ownProps) => {
      const { cardData, readOnly, adminAccess, owner } = ownProps;
      if (!cardData) return null;
      const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at);
      const name = cardData.name || '';
      const description = cardData.description || ' ';

      return {
        name,
        description,
        createdAt,
        readOnly,
        adminAccess,
        aclQueryMode,
        owner,
        cardData,
      };
    },
    [aclQueryMode],
  );

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <DataMapDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
        />
      }
      rowRenderer={makeRowRenderer({ handleCardSelected, getData })}
      getSourceMeta={getSourceMeta}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default DataMapTable;
